<template>
    <div>
        <div class="flex justify-end gap-x-2">
            <div v-if="isDemo">
                <Tippy>
                    <AppButton :disabled="true" type="button">
                        <IconPrinter class="-ml-0.5 mr-2 w-4 h-4" />
                        {{ $t('messages.pdf') }}
                    </AppButton>
                    <template #content>
                        {{ $t('messages.not_available_in_demo') }}
                    </template>
                </Tippy>
            </div>
            <AppButton :href="links.group_print_show" v-else-if="teams.length">
                <IconPrinter class="-ml-0.5 mr-2 w-4 h-4" />
                {{ $t('messages.pdf') }}
            </AppButton>

            <AppButton element="InertiaLink" :href="links.sociogram_show" v-if="links.sociogram_show">
                {{ $t('messages.return_to_sociogram') }}
            </AppButton>

            <AppButton element="InertiaLink" :href="links.sociogram_surveys_index" v-if="links.sociogram_surveys_index">
                {{ $t('messages.sociograms_list') }}
            </AppButton>
        </div>

        <div class="mt-3 flex flex-wrap gap-2">
            <button @click="setSection('team_generator_quick_guide')">
                <BadgePill :color="sections['team_generator_quick_guide'] ? 'indigo' : 'gray'">
                    {{ $t('messages.team_generator_quick_guide') }}
                </BadgePill>
            </button>
            <button @click="setSection('restrictions')">
                <BadgePill :color="sections['restrictions'] ? 'indigo' : 'gray'">
                    {{ $t('messages.teams_generator_restrictions') }}
                </BadgePill>
            </button>
            <button @click="setSection('teams_blackboard')">
                <BadgePill :color="sections['teams_blackboard'] ? 'indigo' : 'gray'">
                    {{ $t('messages.teams_blackboard') }}
                </BadgePill>
            </button>
            <button @click="setSection('drafts_list')" v-if="showDrafts">
                <BadgePill :color="sections['drafts_list'] ? 'indigo' : 'gray'">
                    {{ $tc('messages.drafts', 2) }}
                </BadgePill>
            </button>
            <button @click="setSection('groupings')">
                <BadgePill :color="sections['groupings'] ? 'indigo' : 'gray'">
                    {{ $tc('messages.groupings', 2) }}
                </BadgePill>
            </button>
            <button @click="setSection('sociograms')" v-if="sociogramSurvey !== null">
                <BadgePill :color="sections['sociograms'] ? 'indigo' : 'gray'">
                    {{ $tc('messages.sociograms', 2) }}
                </BadgePill>
            </button>
            <button @click="setSection('sociogram_table')" v-if="table !== null">
                <BadgePill :color="sections['sociogram_table'] ? 'indigo' : 'gray'">
                    {{ $t('messages.sociogram_interactions_table') }}
                </BadgePill>
            </button>
        </div>

        <PageSectionCollapsible
            v-model:is-open="sections.team_generator_quick_guide"
            id="nexa_quick_guide"
            class="mt-5"
        >
            <template v-slot:title>
                {{ $t('messages.team_generator_quick_guide') }}
            </template>
            <template v-slot:subtitle>
                {{ $t('messages.team_generator_quick_guide_subtitle') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <p class="mt-1 whitespace-pre-line col-span-8">
                    {{ $t('messages.team_generator_quick_guide_explanation') }}
                </p>
                <div class="col-span-4 hidden md:block">
                    <img src="/img/team_generator/nexa_guide_image_team_edit.webp" alt="" />
                </div>
            </div>
        </PageSectionCollapsible>

        <PageSectionCollapsible v-model:is-open="sections.restrictions" id="restrictions" class="mt-5">
            <template v-slot:title>
                {{ $t('messages.teams_generator_restrictions') }}
            </template>
            <template v-slot:subtitle>
                {{ $t('messages.teams_generator_restrictions_subtitle') }}
            </template>
            <div class="mt-1">
                {{ $t('messages.teams_generator_starting_point_description') }}
            </div>
            <AppWell>
                <div id="starting_point">
                    <GroupingTeamsDraft
                        v-model="startingPoint"
                        :with-survey="info.with_survey"
                        :all-students="allStudents"
                        id="starting_point"
                        is-edit
                        :title="$t('messages.teams_generator_starting_point_title')"
                        :description="$t('messages.teams_generator_starting_point_description')"
                    />
                </div>
                <div id="negative_restrictions"></div>
            </AppWell>
        </PageSectionCollapsible>

        <PageSectionCollapsible v-model:is-open="sections.teams_blackboard" id="teams_blackboard" class="mt-5">
            <template v-slot:title>
                {{ $t('messages.teams_blackboard') }}
            </template>
            <template v-slot:subtitle>
                <span>{{ $t('messages.teams_blackboard_explanation') }}</span>
                <span v-if="info.with_survey">&nbsp; {{ $t('messages.team_drafts_explanation_with_survey') }}</span>
            </template>
            <div class="flex justify-between items-center">
                <div v-if="isDemo">
                    <Tippy>
                        <AppButton :disabled="true" type="button">
                            {{ $t('messages.save') }}
                        </AppButton>
                        <template #content>
                            {{ $t('messages.not_available_in_demo') }}
                        </template>
                    </Tippy>
                </div>
                <div v-else class="flex gap-2 items-bottom">
                    <form action="" @submit.prevent="submit()">
                        <div class="flex gap-2 items-bottom">
                            <div>
                                <FormLabel :value="$t('messages.name')" for="name" />
                                <FormInput
                                    type="text"
                                    name="name"
                                    id="name"
                                    v-model.number="form.name"
                                    required
                                    maxlength="50"
                                    class="w-40"
                                />
                            </div>
                            <div class="flex flex-col justify-end" v-if="links.update">
                                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
                            </div>
                        </div>
                    </form>
                    <form action="" @submit.prevent="submitNew()" v-if="links.store" class="flex gap-2 items-bottom">
                        <div class="flex flex-col justify-end">
                            <SubmitFormButton
                                :button-text="$t('messages.save_new_as')"
                                :processing="formNew.processing"
                                :desired-action="!links.update"
                            />
                        </div>
                    </form>
                </div>

                <div class="flex justify-end gap-x-2">
                    <AppButton type="button" element="button" @click="saveDraft()" v-if="showDrafts">
                        {{ $t('messages.save_draft') }}
                    </AppButton>
                    <AppButton type="button" element="button" @click="emptyTeams()">
                        {{ $t('messages.empty_teams') }}
                    </AppButton>
                </div>
            </div>

            <div class="flex justify-end">
                <TeamGenerator
                    :students="allStudents"
                    @teams-generated="handleGeneratedTeams"
                    v-if="info.with_survey"
                    :starting-point="startingPoint"
                />
            </div>

            <PageSection id="teams">
                <ProgressBarCurrentMax
                    v-if="overAllEvaluation"
                    :max="overAllEvaluation.total"
                    :current="overAllEvaluation.positive"
                    background-color="red"
                    class="mt-5"
                >
                    {{ overAllEvaluation.positive }} vs -{{ overAllEvaluation.negative }}
                </ProgressBarCurrentMax>

                <div class="mt-5">
                    <div class="md:grid md:grid-cols-3 md:gap-6 bg-white">
                        <div
                            v-for="(team, index) in form.teams"
                            class="p-2 rounded-lg"
                            :class="{
                                'bg-red-200': incompatibleTeams.includes(index),
                                'bg-green-200': compatibleTeams.includes(index),
                                'bg-blue-200': topCompatibleTeams.includes(index),
                                'bg-gray-200': neutralTeams.includes(index),
                            }"
                            style="min-height: 100px"
                            :key="index"
                        >
                            <div v-if="info.with_survey"><TeamSurveyEvaluation :team="team" /></div>
                            <div v-if="info.with_survey">
                                <StudentTeamChangeEvaluation :team="team" :student="movingStudent" />
                            </div>
                            <div class="flex items-center gap-4">
                                <FormInput
                                    required
                                    v-model="team.name"
                                    name="name"
                                    :id="`team_name_${index}`"
                                    type="text"
                                    max="30"
                                    :placeholder="$t('messages.team_name')"
                                    class="mt-1 w-full"
                                />

                                <div class="flex gap-1 items-center">
                                    <button
                                        @click="moveUp(index)"
                                        :disabled="index === 0"
                                        class="disabled:text-gray-200 text-gray-400"
                                        type="button"
                                    >
                                        <IconTriangleFilled class="w-3 h-3" />
                                    </button>
                                    <button
                                        @click="moveDown(index)"
                                        :disabled="index === form.teams.length - 1"
                                        class="disabled:text-gray-200 text-gray-400"
                                        type="button"
                                    >
                                        <IconTriangleInvertedFilled class="w-3 h-3" />
                                    </button>
                                    <button
                                        type="button"
                                        :title="$t('messages.remove')"
                                        @click="deleteTeam(index)"
                                        v-if="team.students.length === 0"
                                    >
                                        <IconTrash class="text-gray-400 w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <draggable
                                    v-model="team.students"
                                    group="students"
                                    class="mt-3 md:grid md:grid-cols-2 md:gap-6 h-full list-group"
                                    style="min-height: 80px"
                                    item-key="id"
                                >
                                    <template #item="{ element: student }">
                                        <div
                                            class="bg-white rounded-xl p-2"
                                            :id="student.id"
                                            :title="student.full_name"
                                            :class="{
                                                'border-indigo-600 ring-2 ring-indigo-600':
                                                    movingStudentId === student.id,
                                            }"
                                        >
                                            <div class="flex shrink-0 space-x-2 items-center">
                                                <img
                                                    :src="student.avatar_or_default"
                                                    alt=""
                                                    class="max-w-none h-16 w-16 rounded-full ring-2 ring-white"
                                                />
                                                <div>
                                                    {{ student.name_or_nickname }}
                                                </div>
                                            </div>
                                            <div v-if="info.with_survey" class="flex gap-1 items-center">
                                                <div>
                                                    <Tippy interactive>
                                                        <IconFileAnalytics
                                                            class="w-6 h-6 inline-block"
                                                            :class="
                                                                student.positive.length + student.negative.length > 0
                                                                    ? 'text-gray-400'
                                                                    : 'text-gray-200'
                                                            "
                                                        />
                                                        <template #content>
                                                            <div :key="student.id" class="mt-3">
                                                                <div>
                                                                    {{ student.name_or_nickname }}
                                                                </div>
                                                                <div class="flex gap-2 items-center">
                                                                    <div>
                                                                        <img
                                                                            :title="student.name_or_nickname"
                                                                            :src="student.avatar_or_default"
                                                                            class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                                                                            alt=""
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <div class="flex gap-1 items-center">
                                                                            <IconThumbUpFilled
                                                                                class="w-8 h-8 text-green-400 mr-3 inline-block"
                                                                                style="color: #68d391"
                                                                            />
                                                                            <StudentsProfileStackFiltered
                                                                                :students="allStudents"
                                                                                :selected="student.positive"
                                                                                class="inline-block h-8"
                                                                            />
                                                                        </div>
                                                                        <div class="flex gap-1 items-center">
                                                                            <IconThumbDownFilled
                                                                                class="w-8 h-8 text-red-400 mr-3 inline-block"
                                                                                style="color: #fc8181"
                                                                            />
                                                                            <StudentsProfileStackFiltered
                                                                                :students="allStudents"
                                                                                :selected="student.negative"
                                                                                class="inline-block h-8"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </template>
                                                    </Tippy>
                                                </div>
                                                <div>
                                                    <Tippy interactive>
                                                        <div class="flex gap-1">
                                                            <div
                                                                v-for="i in team.students.filter((s) =>
                                                                    student.positive.includes(s.id),
                                                                ).length"
                                                                class="shrink-0 w-1.5 h-1.5 rounded-full bg-green-400"
                                                                :key="i"
                                                            />
                                                            <div
                                                                v-for="i in student.positive.filter(
                                                                    (s) =>
                                                                        !team.students.map((ts) => ts.id).includes(s),
                                                                )"
                                                                class="shrink-0 w-1.5 h-1.5 rounded-full bg-green-100"
                                                                :key="i"
                                                            />
                                                        </div>
                                                        <div class="flex gap-1 mt-1">
                                                            <div
                                                                v-for="i in team.students.filter((s) =>
                                                                    student.negative.includes(s.id),
                                                                ).length"
                                                                class="shrink-0 w-1.5 h-1.5 rounded-full bg-red-400"
                                                                :key="i"
                                                            />
                                                            <div
                                                                v-for="i in student.negative.filter(
                                                                    (s) =>
                                                                        !team.students.map((ts) => ts.id).includes(s),
                                                                )"
                                                                class="shrink-0 w-1.5 h-1.5 rounded-full bg-red-100"
                                                                :key="i"
                                                            />
                                                        </div>
                                                        <template #content>
                                                            <div :key="student.id" class="mt-3">
                                                                <div>
                                                                    {{ student.name_or_nickname }}
                                                                </div>
                                                                <div class="flex gap-2 items-center">
                                                                    <div>
                                                                        <img
                                                                            :title="student.name_or_nickname"
                                                                            :src="student.avatar_or_default"
                                                                            class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                                                                            alt=""
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <div class="flex gap-1 items-center">
                                                                            <IconThumbUpFilled
                                                                                class="w-8 h-8 text-green-400 mr-3 inline-block"
                                                                                style="color: #68d391"
                                                                            />
                                                                            <StudentsProfileStackFiltered
                                                                                :students="team.students"
                                                                                :selected="student.positive"
                                                                                class="inline-block h-8"
                                                                            />
                                                                        </div>
                                                                        <div class="flex gap-1 items-center">
                                                                            <IconThumbDownFilled
                                                                                class="w-8 h-8 text-red-400 mr-3 inline-block"
                                                                                style="color: #fc8181"
                                                                            />
                                                                            <StudentsProfileStackFiltered
                                                                                :students="team.students"
                                                                                :selected="student.negative"
                                                                                class="inline-block h-8"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </template>
                                                    </Tippy>
                                                </div>
                                                <div>
                                                    <BadgePill>
                                                        {{ student.sort }}
                                                    </BadgePill>
                                                </div>
                                                <div>
                                                    <button
                                                        @click="toggleMovingStudent(student)"
                                                        type="button"
                                                        :title="$t('messages.analyze')"
                                                    >
                                                        <IconZoomQuestion
                                                            class="h-6 w-6"
                                                            :class="
                                                                movingStudentId === student.id
                                                                    ? 'text-indigo-400'
                                                                    : 'text-gray-400'
                                                            "
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </div>
                        <div>
                            <button @click="addTeam()" style="min-height: 100px" :title="$t('messages.add_team')">
                                <PlusButton />
                            </button>
                        </div>
                    </div>
                </div>
            </PageSection>

            <PageSection id="students_not_in_teams" class="mt-5">
                <template v-slot:title>
                    {{ $t('messages.students_not_in_teams') }}
                </template>
                <div class="mt-5 bg-gray-200 p-4">
                    <draggable
                        class="md:grid md:grid-cols-6 md:gap-6 rounded-lg p-2"
                        style="min-height: 100px"
                        :list="withoutTeam.students"
                        group="students"
                        item-key="id"
                    >
                        <template #item="{ element: student }">
                            <div class="bg-white rounded-xl p-2" :id="student.id">
                                <div class="flex shrink-0 space-x-2 items-center">
                                    <img
                                        :src="student.avatar_or_default"
                                        alt=""
                                        class="max-w-none h-16 w-16 rounded-full ring-2 ring-white"
                                    />
                                    <div>
                                        {{ student.name_or_nickname }}
                                    </div>
                                </div>
                                <div v-if="info.with_survey" class="flex gap-1 items-center">
                                    <div>
                                        <Tippy interactive>
                                            <IconFileAnalytics
                                                class="w-6 h-6 inline-block"
                                                :class="
                                                    student.positive.length + student.negative.length > 0
                                                        ? 'text-gray-400'
                                                        : 'text-gray-200'
                                                "
                                            />

                                            <template #content>
                                                <div :key="student.id" class="mt-3">
                                                    <div>
                                                        {{ student.name_or_nickname }}
                                                    </div>
                                                    <div class="flex gap-2 items-center">
                                                        <div>
                                                            <img
                                                                :title="student.name_or_nickname"
                                                                :src="student.avatar_or_default"
                                                                class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                                                                alt=""
                                                            />
                                                        </div>
                                                        <div>
                                                            <div class="flex gap-1 items-center">
                                                                <IconThumbUpFilled
                                                                    class="w-8 h-8 text-green-400 mr-3 inline-block"
                                                                    style="color: #68d391"
                                                                />
                                                                <StudentsProfileStackFiltered
                                                                    :students="allStudents"
                                                                    :selected="student.positive"
                                                                    class="inline-block h-8"
                                                                />
                                                            </div>
                                                            <div class="flex gap-1 items-center">
                                                                <IconThumbDownFilled
                                                                    class="w-8 h-8 text-red-400 mr-3 inline-block"
                                                                    style="color: #fc8181"
                                                                />
                                                                <StudentsProfileStackFiltered
                                                                    :students="allStudents"
                                                                    :selected="student.negative"
                                                                    class="inline-block h-8"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </Tippy>
                                    </div>
                                    <div>
                                        <div class="flex gap-1">
                                            <div
                                                v-for="i in student.positive.length"
                                                class="shrink-0 w-1.5 h-1.5 rounded-full bg-green-400"
                                                :key="i"
                                            />
                                        </div>
                                        <div class="flex gap-1 mt-1">
                                            <div
                                                v-for="i in student.negative.length"
                                                class="shrink-0 w-1.5 h-1.5 rounded-full bg-red-400"
                                                :key="i"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <BadgePill>
                                            {{ student.sort }}
                                        </BadgePill>
                                    </div>
                                    <div>
                                        <button
                                            @click="toggleMovingStudent(student)"
                                            type="button"
                                            :title="$t('messages.analyze')"
                                        >
                                            <IconZoomQuestion
                                                class="h-6 w-6"
                                                :class="
                                                    movingStudentId === student.id ? 'text-indigo-400' : 'text-gray-400'
                                                "
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </draggable>
                    <div v-if="withoutTeam.students.length === 0" class="text-gray-700">
                        {{ $t('messages.all_students_in_teams') }}
                    </div>
                </div>
            </PageSection>
        </PageSectionCollapsible>

        <PageSectionCollapsible v-model:is-open="sections.groupings" id="groupings" class="mt-5">
            <template v-slot:title>
                {{ $tc('messages.groupings', 2) }}
            </template>
            <template v-slot:subtitle>
                {{ $t('messages.team_groupings_explanation') }}
            </template>

            <EmptyState :title="$t('messages.no_groupings_yet')" with-icon v-if="groupings.length === 0" />

            <AppWell v-else>
                <div class="flex flex-col gap-4">
                    <AppCard v-for="grouping in groupings" :key="grouping.id">
                        <div class="flex justify-between">
                            <div class="flex gap-1 items-center">
                                <IconStarFilled v-if="grouping.is_main" class="h-4 w-4 text-gray-400" />
                                <IconEye v-if="grouping.is_visible" class="h-4 w-4 text-gray-400" />
                                <IconStar v-else class="h-4 w-4 text-gray-400" />
                                <div>
                                    {{ grouping.name }}
                                </div>
                                <div class="text-gray-500 text-sm">
                                    ({{
                                        $tc('messages.teams_count', grouping.teams_count, {
                                            count: grouping.teams_count,
                                        })
                                    }}
                                    )
                                </div>
                                <div class="text-gray-500 text-sm">
                                    {{ grouping.teacher_name }}
                                </div>
                            </div>

                            <div class="flex gap-1 items-center">
                                <InertiaSquareLinkButton
                                    method="patch"
                                    :action="grouping.is_visible ? 'eye_closed' : 'eye'"
                                    :title="
                                        grouping.is_visible
                                            ? $t('messages.set_as_not_visible_for_students')
                                            : $t('messages.set_as_visible_for_students')
                                    "
                                    as="button"
                                    :href="grouping.links.toggle_visible"
                                />
                                <InertiaSquareLinkButton
                                    method="patch"
                                    action="star"
                                    as="button"
                                    :title="$t('messages.set_as_main_grouping')"
                                    :href="grouping.links.set_main"
                                    :disabled="grouping.is_main"
                                />
                                <InertiaSquareLinkButton
                                    action="edit"
                                    :title="$t('messages.edit')"
                                    :href="grouping.links.edit"
                                    :disabled="grouping.is_current"
                                />
                                <InertiaSquareDeleteButton
                                    :title="$t('messages.delete')"
                                    :href="grouping.links.delete"
                                    :confirm-text="$t('messages.are_you_sure?')"
                                    :class="{ invisible: !grouping.links.delete }"
                                />
                            </div>
                        </div>
                        <GroupingTeamsDraft
                            v-model="grouping.teams"
                            :with-survey="info.with_survey"
                            :all-students="allStudents"
                            :id="grouping.id"
                        />
                    </AppCard>
                </div>
            </AppWell>
        </PageSectionCollapsible>

        <PageSectionCollapsible v-model:is-open="sections.drafts_list" id="drafts_list" class="mt-5" v-if="showDrafts">
            <template v-slot:title>
                {{ $tc('messages.drafts', 2) }}
            </template>
            <template v-slot:subtitle>
                {{ $t('messages.team_drafts_explanation') }}
            </template>

            <EmptyState :title="$t('messages.no_drafts_yet')" with-icon v-if="drafts.length === 0" />

            <AppWell v-else>
                <div class="flex flex-col gap-4">
                    <AppCard v-for="(draft, index) in drafts" :key="draft.id">
                        <div class="flex justify-between">
                            <div>{{ $t('messages.draft') }} #{{ draft.id }}</div>
                            <div>
                                <button type="button" :title="$t('messages.restore')" @click="restoreDraft(index)">
                                    <IconRestore class="text-gray-700 pr-1 h-6 w-6" />
                                </button>
                                <button type="button" :title="$t('messages.remove')" @click="deleteDraft(index)">
                                    <IconTrash class="text-gray-700 pr-1 h-6 w-6" />
                                </button>
                            </div>
                        </div>
                        <GroupingTeamsDraft
                            v-model="draft.teams"
                            :with-survey="info.with_survey"
                            :all-students="allStudents"
                            :id="index"
                        />
                    </AppCard>
                </div>
            </AppWell>
        </PageSectionCollapsible>

        <PageSectionCollapsible
            id="sociograms"
            class="mt-5"
            v-model:is-open="sections.sociograms"
            v-if="sociogramSurvey !== null"
        >
            <template v-slot:title>
                {{ $t('messages.for_consultation') }}: {{ $tc('messages.sociograms', 2) }}
            </template>
            <template v-slot:subtitle>
                {{ $t('messages.sociogram_section_team_generator_subtitle') }}
            </template>

            <div v-for="(item, itemIndex) in sociogramSurvey.items" :key="item.id">
                <AppWell>
                    <AppCard>
                        <details :open="itemIndex === 0" class="p-1">
                            <summary>
                                <span class="font-semibold">{{ item.title }}</span>
                            </summary>
                            <div class="mt-1">
                                <template v-if="item.type === 'relationship'">
                                    <NetworkGraphSociogramStudents
                                        :students="item.students"
                                        :edges="item.edges"
                                        :is-negative="item.subtype === 'negative'"
                                    />
                                </template>
                            </div>
                        </details>
                    </AppCard>
                </AppWell>
            </div>
        </PageSectionCollapsible>

        <PageSectionCollapsible
            v-model:is-open="sections.sociogram_table"
            id="sociogram_table"
            v-if="table !== null"
            class="mt-5"
        >
            <template v-slot:title>
                {{ $t('messages.for_consultation') }}: {{ $t('messages.sociogram_interactions_table') }}
            </template>
            <template v-slot:subtitle>
                <div>{{ $t('messages.sociogram_interactions_table_explanation') }}</div>
            </template>
            <div class="flex flex-col">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th
                                        class="max-w-xs px-1 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        v-for="(header, headerIndex) in table.headers"
                                        :key="header.id"
                                        :class="{
                                            'bg-indigo-200': hoverColumn === headerIndex,
                                        }"
                                    >
                                        <img
                                            :title="header.name_or_nickname"
                                            :src="header.avatar_or_default"
                                            class="relative z-30 inline-block h-6 w-6 rounded-full ring-2 ring-white"
                                            alt=""
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(row, index) in table.rows"
                                    :key="row.student.id"
                                    class="group"
                                >
                                    <td
                                        class="px-1 py-1 whitespace-nowrap text-sm text-center leading-5 text-gray-500"
                                        :class="{
                                            'group-hover:bg-indigo-300': index % 2 === 0 && hoverColumn > hoverRow,
                                            'group-hover:bg-indigo-200': index % 2 === 1 && hoverColumn > hoverRow,
                                        }"
                                    >
                                        <img
                                            :title="row.student.name_or_nickname"
                                            :src="row.student.avatar_or_default"
                                            class="relative z-30 inline-block h-6 w-6 rounded-full ring-2 ring-white"
                                            alt=""
                                        />
                                    </td>
                                    <td
                                        class="px-1 py-1 whitespace-nowrap text-sm leading-5 text-gray-500 text-center tabular-nums"
                                        v-for="({ column, title }, i) in row.columns"
                                        :key="i"
                                        :title="title"
                                        :class="{
                                            'bg-gray-300': column === 'below_triangle' && index % 2 === 0,
                                            'group-hover:bg-indigo-300':
                                                column === 'below_triangle' &&
                                                index % 2 === 0 &&
                                                hoverColumn > hoverRow,
                                            'bg-gray-200': column === 'below_triangle' && index % 2 === 1,
                                            'group-hover:bg-indigo-200':
                                                column === 'below_triangle' &&
                                                index % 2 === 1 &&
                                                hoverColumn > hoverRow,
                                            'bg-indigo-200':
                                                column === null &&
                                                (hoverColumn === i || index === hoverRow) &&
                                                hoverColumn > hoverRow &&
                                                index <= hoverRow &&
                                                i <= hoverColumn,
                                            'bg-green-200': column === 2,
                                            'bg-green-100': column === 1,
                                            'bg-yellow-200': column === 0,
                                            'bg-red-100': column === -1,
                                            'bg-red-200': column === -2,
                                        }"
                                        @mouseover="
                                            hoverColumn = i;
                                            hoverRow = index;
                                        "
                                        @mouseleave="
                                            hoverColumn = null;
                                            hoverRow = null;
                                        "
                                    >
                                        <span v-if="column !== 'below_triangle'">
                                            {{ column }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageSectionCollapsible>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import AppButton from '@/components/AppButton.vue';
import draggable from 'vuedraggable';
import PlusButton from '@/components/Forms/PlusButton.vue';
import TeamSurveyEvaluation from '@/Pages/Teacher/Gamification/Groups/MainTeams/partials/TeamSurveyEvaluation.vue';
import ProgressBarCurrentMax from '@/components/Gamification/ProgressBarCurrentMax.vue';
import { Tippy } from 'vue-tippy';

import {
    IconEye,
    IconFileAnalytics,
    IconPrinter,
    IconRestore,
    IconStar,
    IconStarFilled,
    IconThumbDownFilled,
    IconThumbUpFilled,
    IconTrash,
    IconTriangleFilled,
    IconTriangleInvertedFilled,
    IconZoomQuestion,
} from '@tabler/icons-vue';
import StudentsProfileStackFiltered from '@/components/StudentsProfileStackFiltered.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import GroupingTeamsDraft from '@/Pages/Teacher/Gamification/Groups/MainTeams/partials/GroupingTeamsDraft.vue';
import BadgePill from '@/components/BadgePill.vue';
import TeamGenerator from '@/Pages/Teacher/Gamification/Groups/MainTeams/partials/TeamGenerator.vue';
import StudentTeamChangeEvaluation from '@/Pages/Teacher/Gamification/Groups/MainTeams/partials/StudentTeamChangeEvaluation.vue';
import PageSection from '@/Shared/PageSection.vue';
import PageSectionCollapsible from '@/Shared/PageSectionCollapsible.vue';
import SubmitFormButton from '../../../../Activities/Subactivities/SubmitFormButton.vue';
import EmptyState from '../../../../../../components/EmptyState.vue';
import NetworkGraphSociogramStudents from '../../../../../../components/pageSpecific/NetworkGraphSociogramStudents.vue';
import InertiaSquareDeleteButton from '../../../../../../components/InertiaSquareDeleteButton.vue';
import InertiaSquareLinkButton from '../../../../../../components/InertiaSquareLinkButton.vue';
import FormLabel from '../../../../../../components/Forms/FormLabel.vue';

export default {
    components: {
        FormLabel,
        InertiaSquareLinkButton,
        InertiaSquareDeleteButton,
        NetworkGraphSociogramStudents,
        EmptyState,
        IconPrinter,
        SubmitFormButton,
        PageSectionCollapsible,
        PageSection,
        StudentTeamChangeEvaluation,
        TeamGenerator,
        IconTriangleInvertedFilled,
        IconTriangleFilled,
        IconZoomQuestion,
        BadgePill,
        IconTrash,
        IconRestore,
        GroupingTeamsDraft,
        AppCard,
        AppWell,
        StudentsProfileStackFiltered,
        IconThumbUpFilled,
        IconThumbDownFilled,
        IconFileAnalytics,
        Tippy,
        ProgressBarCurrentMax,
        TeamSurveyEvaluation,
        FormInput,
        AppButton,
        draggable,
        PlusButton,
        IconStarFilled,
        IconEye,
        IconStar,
    },
    layout: Layout,
    name: 'TeamGeneratorPage',
    props: {
        teams: {
            type: Array,
            required: true,
        },
        allStudents: {
            type: Array,
            required: true,
        },
        studentsNotInTeam: {
            type: Array,
            required: true,
        },
        groupings: {
            type: Array,
            required: true,
        },
        grouping: {
            type: Object,
            required: true,
        },
        info: {
            type: Object,
            required: true,
        },
        links: {
            type: Object,
            required: true,
        },
        table: {
            type: [Object, null],
            required: true,
        },
        sociogramSurvey: {
            type: [Object, null],
            required: true,
        },
        isDemo: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            form: this.$inertia.form({
                name: this.grouping.name,
                teams: JSON.parse(JSON.stringify(this.teams)),
            }),
            formNew: this.$inertia.form({
                name: null,
            }),
            withoutTeam: {
                students: this.studentsNotInTeam,
            },
            draftsCount: 0,
            movingStudent: null,
            movingStudentId: null,
            drafts: [],
            startingPoint: [],
            hoverColumn: null,
            hoverRow: null,
            showDrafts: null,
            sections: {
                team_generator_quick_guide: true,
                teams_blackboard: true,
                restrictions: false,
                groupings: false,
                drafts_list: false,
                sociograms: false,
                sociogram_table: false,
            },
        };
    },
    methods: {
        setSection(section) {
            [
                'team_generator_quick_guide',
                'teams_blackboard',
                'restrictions',
                'groupings',
                'drafts_list',
                'sociograms',
                'sociogram_table',
            ].forEach((s) => {
                if (s === section) {
                    this.sections[s] = !this.sections[s];
                } else {
                    this.sections[s] = false;
                }
            });
        },
        addTeam() {
            this.form.teams.push({
                id: null,
                name: null,
                students: [],
            });
        },
        moveUp(index) {
            this.form.teams = this.swapItems(this.form.teams, index, index - 1);
        },
        handleGeneratedTeams(teams) {
            this.form.teams = teams;
            this.withoutTeam.students = [];
        },
        moveDown(index) {
            this.form.teams = this.swapItems(this.form.teams, index, index + 1);
        },
        swapItems(a, i, j) {
            const newArray = [...a];
            newArray[i] = a[j];
            newArray[j] = a[i];
            return newArray;
        },
        saveDraft() {
            this.draftsCount += 1;
            this.drafts.push({
                id: this.draftsCount,
                teams: JSON.parse(JSON.stringify(this.form.teams)),
                withoutTeam: JSON.parse(JSON.stringify(this.withoutTeam)),
            });
        },
        deleteDraft(index) {
            this.drafts.splice(index, 1);
        },
        restoreDraft(index) {
            this.form.teams = JSON.parse(JSON.stringify(this.drafts[index].teams));
            this.withoutTeam = JSON.parse(JSON.stringify(this.drafts[index].withoutTeam));
        },
        toggleMovingStudent(student) {
            if (student.id !== this.movingStudentId) {
                this.movingStudentId = student.id;
                this.movingStudent = student;
            } else {
                this.movingStudentId = null;
                this.movingStudent = null;
            }
        },
        getStudentTeamCompatibility(student, team) {
            let compatibility = {
                positive: 0,
                negative: 0,
                positive_inverse: 0,
                negative_inverse: 0,
                total: 0,
                positive_total: 0,
                negative_total: 0,
            };

            team.students.forEach((s) => {
                compatibility.positive += student.positive.includes(s.id);
                compatibility.negative -= student.negative.includes(s.id);
                compatibility.positive_inverse += s.positive.includes(student.id);
                compatibility.negative_inverse -= s.negative.includes(student.id);
            });

            compatibility.positive_total = compatibility.positive + compatibility.positive_inverse;
            compatibility.negative_total = compatibility.negative + compatibility.negative_inverse;
            compatibility.total = compatibility.positive_total + compatibility.negative_total;
            return compatibility;
        },
        submit() {
            this.form.patch(this.links.update, {
                onSuccess: () => (this.form.teams = this.teams),
            });
        },
        submitNew() {
            this.formNew
                .transform((data) => {
                    data.name = this.form.name;
                    data.teams = this.form.teams;
                    return data;
                })
                .post(this.links.store, {
                    onSuccess: () => (this.form.teams = this.teams),
                });
        },
        emptyTeams() {
            this.form.teams.forEach((team) => {
                while (team.students.length > 0) {
                    const student = team.students.pop();
                    this.withoutTeam.students.push(student);
                }
            });
        },
        deleteTeam(index) {
            this.form.teams.splice(index, 1);
        },
    },

    mounted() {
        if (!this.teams.length) {
            this.addTeam();
        }

        this.withoutTeam.students.sort((a, b) => b.sort - a.sort);
    },
    computed: {
        teamsWithCompatibility: function () {
            const teamsWithCompatibility = [];
            if (this.movingStudent) {
                return this.form.teams.map((team, index) => {
                    const compatibility = this.getStudentTeamCompatibility(this.movingStudent, team);
                    return {
                        team,
                        compatibility,
                        index,
                    };
                });
            }

            return teamsWithCompatibility;
        },
        compatibilityTopScore: function () {
            return (
                this.teamsWithCompatibility
                    .filter((tc) => {
                        return tc.compatibility.negative_total === 0 && tc.compatibility.positive_total > 0;
                    })
                    .reduce((acc, tc) => Math.max(acc, tc.compatibility.positive_total), -Infinity) || -Infinity
            );
        },
        topCompatibleTeams: function () {
            return this.teamsWithCompatibility
                .filter((tc) => {
                    return tc.compatibility.negative_total === 0;
                })
                .filter((tc) => {
                    return tc.compatibility.positive_total === this.compatibilityTopScore;
                })
                .map((tc) => tc.index);
        },
        compatibleTeams: function () {
            return this.teamsWithCompatibility
                .filter((tc) => {
                    return tc.compatibility.negative_total === 0 && tc.compatibility.positive_total > 0;
                })
                .filter((tc) => {
                    return tc.compatibility.positive_total !== this.compatibilityTopScore;
                })
                .map((tc) => tc.index);
        },
        neutralTeams: function () {
            const teamsIds = this.compatibleTeams.concat(this.incompatibleTeams).concat(this.topCompatibleTeams);

            return this.form.teams
                .map((t, index) => {
                    return index;
                })
                .filter((index) => {
                    return !teamsIds.includes(index);
                });
        },
        incompatibleTeams: function () {
            return this.teamsWithCompatibility
                .filter((tc) => {
                    return tc.compatibility.negative < 0 || tc.compatibility.negative_inverse < 0;
                })
                .map((tc) => tc.index);
        },
        overAllEvaluation: function () {
            if (!this.info.with_survey) {
                return null;
            }

            const evaluation = {
                positive: 0,
                negative: 0,
                total: 0,
            };

            this.form.teams.forEach((team) => {
                const studentIds = team.students.map((s) => s.id);
                team.students.forEach((student) => {
                    const positive = student.positive.filter((p) => studentIds.includes(p));
                    const negative = student.negative.filter((n) => studentIds.includes(n));

                    evaluation.positive += positive.length;
                    evaluation.negative += negative.length;
                    evaluation.total += positive.length + negative.length;
                });
            });

            return evaluation;
        },
    },
};
</script>
